/**
 * security-rag-wiki-fixtures.ts
 *
 * Seed Fixtures for LLMWiki, RAG Vector Indexer, CRM & Tactical ActionPlanner (Sprint CMS-RAG-01):
 * - RAG/LLMWiki Markdown Documents (Public Knowledge, Internal Operations SOP, Commercial Sales Playbook).
 * - CRM Organizations, Persons, and Pipeline Opportunities.
 * - Tactical ActionPlanner / Kanban Tasks.
 * - Component-Level JSON Block Schemas for CMS.
 */

export interface SecurityWikiDocumentFixture {
  readonly id: string;
  readonly title: string;
  readonly category: 'public_knowledge' | 'internal_operations' | 'commercial_playbook';
  readonly audience: string;
  readonly classification?: string;
  readonly tags: readonly string[];
  readonly content: string;
  readonly trustScore: number;
}

export const SECURITY_RAG_WIKI_DOCUMENTS: readonly SecurityWikiDocumentFixture[] = [
  // Document 1: Public Site Concierge / Inbound Sales Assistant
  {
    id: 'wiki-sec-public-001',
    title: 'Sovereign Asset Defense - Client Facing Knowledge & Service Standards',
    category: 'public_knowledge',
    audience: 'website_assistant, inbound_sales',
    trustScore: 1.0,
    tags: ['vault_custody', 'cit_logistics', 'pricing_model', 'compliance', 'gold_transit'],
    content: `# Sovereign Asset Defense: Core Client Briefing Document

## 1. Overview
Sovereign Asset Defense Ltd is a Uganda-licensed institutional security and asset logistics firm operating a 24/7 Security Operations Centre (SOC) in Kampala. We provide:
1. Private Bank-Grade Vault Custody (Land titles, precious minerals, corporate archive documents).
2. Cash-in-Transit (CIT) & Bullion Logistics (B6/B7 armored transport across all Uganda districts).
3. AI Video Analytics & Managed SOC monitoring.
4. Industrial & Mining Concession Security (Albertine Graben Oil & Gas, Karamoja Gold concessions).

## 2. Private Vault Custody Specifications
- **Facility Rating**: Class-4 Drill & Torch Resistant Modular Vault.
- **Access Protocol**: Dual-Custody Biometric. Requires simultaneous client biometric identity (Iris/Fingerprint) plus authorized Duty Controller digital token.
- **Accepted Assets**: Registered Land Title Deeds, Mining Concession Agreements, Legal Wills, Bullion/Gold Bars, Hardware Crypto Wallets, Corporate Data Tape Backups.
- **Excluded Items**: Perishables, uncertified hazardous chemicals, unlicensed firearms/munitions.

## 3. Cash-in-Transit (CIT) & Gold Logistics
- **Fleet Standards**: Ballistic Level B6 & B7 Armored Vehicles.
- **Tracking**: Continuous dual-satellite (Iridium) and cellular mesh telemetry.
- **Geographic Reach**: Daily routes covering Greater Kampala Metropolitan Area (GKMA), Jinja, Mbarara, Gulu, Mbale, Fort Portal, and direct airside clearance at Entebbe International Airport (EBB).
- **Insurance**: All transit consignments are backed by comprehensive Lloyd's-underwritten Cash-in-Transit & Specie Insurance.

## 4. Frequently Asked Questions (FAQ) for Inbound Inquiries
- **Q: Can international clients store assets in the Kampala vault?**
  *A: Yes, subject to standard Financial Intelligence Authority (FIA) and statutory KYC verification.*
- **Q: How quickly can an emergency CIT transport be mobilized?**
  *A: Pre-enrolled commercial clients can request tactical pickup within 45 minutes inside GKMA.*
- **Q: Are site visits permitted before booking vault space?**
  *A: Facility inspections are available for accredited corporate trustees and legal representatives by appointment.*`,
  },

  // Document 2: Internal Operations & SOC Management Assistant
  {
    id: 'wiki-sec-ops-002',
    title: 'SOP 402 - SOC Threat Escalation, Convoy Intercept & Dual-Custody Procedures',
    category: 'internal_operations',
    audience: 'management_assistant, soc_operators, duty_captains',
    classification: 'STRICTLY_CONFIDENTIAL',
    trustScore: 1.0,
    tags: ['soc_escalation', 'convoy_intercept', 'dual_custody', 'threat_matrix'],
    content: `# Standard Operating Procedure 402: Tactical Operations & Vault Access

## 1. 24/7 SOC Threat Level Matrix & Automated Actions
- **GREEN (Nominal)**: Normal telemetry across all CIT convoys and perimeter cameras. Standard logging; 60-second ping intervals. Continuous monitoring.
- **YELLOW (Elevated)**: CIT convoy off-route by > 300m without dispatcher route clearance; or camera edge AI detects loitering > 90s near perimeter gate. Elevate camera resolution; notify convoy Commander via encrypted VHF channel. Operator SLA < 30 Seconds.
- **RED (Critical)**: Vehicle panic button actuated, ballistic impact sensor tripped, or unauthorized dual-key attempt on Vault Gate. Auto-lock all facility blast doors; dispatch Armed Quick Reaction Force (QRF); notify Uganda Police 999 Command; initiate remote vehicle engine disablement if safe. Operator SLA < 5 Seconds.

## 2. Dual-Custody Vault Access Checklist (Physical Protocol)
Every opening of Client Locker or Corporate Vault Bay must execute the following non-negotiable sequence:
1. **Visitor Enrollment**: Verify National ID / Passport against pre-approved Biometric Whitelist in Sovereign CRM.
2. **Access Token Generation**: Generate single-use 15-minute challenge nonce in msng3r.plugin.hardware.
3. **Physical Mantrap Gate**: Visitor enters Mantrap 1. Weight sensor verifies single occupant (< 120 kg).
4. **Simultaneous Biometric Interlock**: Client places index finger and iris scan on Terminal A. Duty Vault Controller verifies identity on Terminal B.
5. **Audit Record Commit**: The hardware controller registers an immutable event hardware.vault.unlocked containing actor principal IDs and SHA-256 evidence digest.`,
  },

  // Document 3: Sales Intelligence & Proposal Generation Assistant
  {
    id: 'wiki-sec-sales-003',
    title: 'B2B Commercial Proposal Playbook - Mining, Oil & Banking Sectors',
    category: 'commercial_playbook',
    audience: 'sales_assistant, account_executives, commercial_directors',
    trustScore: 1.0,
    tags: ['b2b_sales', 'pricing_benchmarks', 'oil_gas', 'mining_bullion'],
    content: `# Institutional Security Sales Playbook & Pricing Framework

## 1. High-Probability Client Personas & Pain Points
1. **Gold Refineries & Karamoja/Entebbe Traders**:
   - *Pain Point*: Intercept risk along highway transit from regional extraction zones to Entebbe export hubs.
   - *Winning Value Prop*: B7 Armored Transport with direct customs bonded airside handover and Lloyd's specie coverage.
2. **Albertine Graben Oil & Gas EPC Joint Ventures (Tilenga / Kingfisher)**:
   - *Pain Point*: High compliance hurdle for international joint-venture audits, contractor credential leaks, remote perimeter vulnerabilities.
   - *Winning Value Prop*: AI-assisted radar/camera perimeter arrays, biometric turnstiles, ISO-aligned zero-trust access logging.
3. **Commercial Banks & Tier-1 Microfinance Consortia**:
   - *Pain Point*: High cost of owning/maintaining internal armored vehicle fleets; branch vault security audits.
   - *Winning Value Prop*: Fully outsourced CIT with guaranteed 99.98% transit SLA and daily cash-sweep reconciliation.

## 2. Standard Pricing & Retainer Benchmarks (USD)
- **Private Vault Locker (HNWI / Legal Deeds)**: $1,200 – $4,500 / year (depending on box volume and insurance coverage tier).
- **Corporate Bulk Document Archive (Fireproof/Inert Gas)**: $850 / month per storage bay.
- **Dedicated Daily Bank CIT Route (GKMA)**: $3,500 – $7,500 / month per armored vehicle unit.
- **Ad-Hoc Regional Bullion Escort (Upcountry to Kampala/Entebbe)**: $1,800 + $2.50 / km per mission.
- **Turnkey AI SOC Monitoring Service**: $1,500 / month per facility (up to 32 neural camera channels).`,
  },
];

export const SECURITY_CRM_FIXTURES = {
  organizations: [
    {
      organizationId: 'org-sec-001',
      tenantId: 'tenant-sovereign-sec',
      legalName: 'Albertine Rift Exploration JV',
      tradingName: 'Albertine Energy Logistics',
      industryCode: '211120',
      taxIdentifier: 'URA-TIN-100294819',
      creditRiskTier: 'tier_1_prime',
      totalSettledLtvMinor: 8400000,
      currency: 'USD',
      tags: ['oil_gas', 'albertine_graben', 'critical_infrastructure', 'epc_contractor'],
    },
    {
      organizationId: 'org-sec-002',
      tenantId: 'tenant-sovereign-sec',
      legalName: 'Victoria Bullion Refineries Ltd',
      tradingName: 'Victoria Refineries Entebbe',
      industryCode: '331410',
      taxIdentifier: 'URA-TIN-994810238',
      creditRiskTier: 'tier_1_prime',
      totalSettledLtvMinor: 12500000,
      currency: 'USD',
      tags: ['gold_mining', 'cit_logistics', 'bullion_export', 'high_value'],
    },
    {
      organizationId: 'org-sec-003',
      tenantId: 'tenant-sovereign-sec',
      legalName: 'Mukasa, Katende & Ssempebwa Advocates',
      tradingName: 'MKS Legal Trustees',
      industryCode: '541110',
      taxIdentifier: 'URA-TIN-448201934',
      creditRiskTier: 'tier_2_standard',
      totalSettledLtvMinor: 2200000,
      currency: 'USD',
      tags: ['legal_estate', 'land_title_custody', 'private_vault', 'hnwi'],
    },
  ],
  persons: [
    {
      personId: 'per-sec-101',
      tenantId: 'tenant-sovereign-sec',
      primaryOrganizationId: 'org-sec-001',
      displayName: 'Eng. Patrick Byamukama',
      primaryEmail: 'p.byamukama@albertine-energy.ug',
      normalizedPhoneE164: '+256772100450',
      lifecycleStage: 'opportunity',
      leadScore: 85,
      consentSummary: 'explicit_marketing_and_transactional_granted',
    },
    {
      personId: 'per-sec-102',
      tenantId: 'tenant-sovereign-sec',
      primaryOrganizationId: 'org-sec-002',
      displayName: 'Rashid Al-Mansoor',
      primaryEmail: 'r.almansoor@victoria-refineries.com',
      normalizedPhoneE164: '+256701994820',
      lifecycleStage: 'customer',
      leadScore: 95,
      consentSummary: 'explicit_transactional_granted',
    },
    {
      personId: 'per-sec-103',
      tenantId: 'tenant-sovereign-sec',
      primaryOrganizationId: 'org-sec-003',
      displayName: 'Counsel Florence Nabukeera',
      primaryEmail: 'f.nabukeera@mks-advocates.ug',
      normalizedPhoneE164: '+256782440192',
      lifecycleStage: 'sales_qualified',
      leadScore: 75,
      consentSummary: 'explicit_transactional_granted',
    },
  ],
  opportunities: [
    {
      opportunityId: 'opp-sec-801',
      tenantId: 'tenant-sovereign-sec',
      title: 'Albertine Pipeline Base Camp - Perimeter AI & Biometric Gate',
      organizationId: 'org-sec-001',
      primaryContactId: 'per-sec-101',
      stage: 'negotiation_terms',
      winProbabilityPercent: 75,
      expectedValueMinor: 14500000,
      currency: 'USD',
      weightedValueMinor: 10875000,
      expectedCloseDate: '2026-09-30',
      ownerPrincipalId: 'principal-sales-dir-01',
    },
    {
      opportunityId: 'opp-sec-802',
      tenantId: 'tenant-sovereign-sec',
      title: 'Daily Armored CIT Escort Route (Entebbe Airport - Kampala Refinery)',
      organizationId: 'org-sec-002',
      primaryContactId: 'per-sec-102',
      stage: 'closed_won',
      winProbabilityPercent: 100,
      expectedValueMinor: 8500000,
      currency: 'USD',
      weightedValueMinor: 8500000,
      expectedCloseDate: '2026-08-15',
      ownerPrincipalId: 'principal-cit-director-01',
    },
    {
      opportunityId: 'opp-sec-803',
      tenantId: 'tenant-sovereign-sec',
      title: 'HNWI Land Title Vault Registry - 50 Dedicated Climate Lockers',
      organizationId: 'org-sec-003',
      primaryContactId: 'per-sec-103',
      stage: 'proposal_quote',
      winProbabilityPercent: 50,
      expectedValueMinor: 3600000,
      currency: 'USD',
      weightedValueMinor: 1800000,
      expectedCloseDate: '2026-10-15',
      ownerPrincipalId: 'principal-vault-mgr-01',
    },
  ],
};

export const SECURITY_ACTION_PLANNER_TASKS = [
  {
    taskId: 'TSK-SEC-1001',
    domain: 'physical_security_audit',
    title: 'Execute Physical Vulnerability Assessment - Albertine Base Camp',
    boardId: 'board-ops-tactical',
    laneId: 'in_progress',
    priority: 'HIGH',
    assignedPrincipalId: 'principal-security-auditor-02',
    linkedOpportunityId: 'opp-sec-801',
    dueAtIso: '2026-08-25T14:00:00Z',
    checklist: [
      { item: 'Inspect 4.2km electrified fence perimeter & microwave radar sensors', completed: true },
      { item: 'Test night-vision thermal camera blindspots on West Gate', completed: true },
      { item: 'Evaluate contractor biometric turnstile throughput at shift change', completed: false },
      { item: 'Generate ISO 27001 physical audit summary report', completed: false },
    ],
  },
  {
    taskId: 'TSK-SEC-1002',
    domain: 'cit_logistics_dispatch',
    title: 'Dispatch Armored Bullion Escort Mission #EBB-994',
    boardId: 'board-ops-tactical',
    laneId: 'scheduled',
    priority: 'CRITICAL',
    assignedPrincipalId: 'principal-cit-commander-01',
    linkedOpportunityId: 'opp-sec-802',
    dueAtIso: '2026-08-22T06:00:00Z',
    checklist: [
      { item: 'Conduct B7 ballistic tire pressure and telematics sensor check', completed: false },
      { item: 'Sync dual satellite Iridium & VHF encrypted mesh channels with SOC', completed: false },
      { item: 'Issue digital tamper seals and record QR digests for 12 Bullion Boxes', completed: false },
      { item: 'Execute biometric sign-off with Entebbe Airside Customs Controller', completed: false },
    ],
  },
  {
    taskId: 'TSK-SEC-1003',
    domain: 'vault_custody_onboarding',
    title: 'Biometric Onboarding & Deed Ingestion - MKS Legal Estate Trust',
    boardId: 'board-ops-tactical',
    laneId: 'ready_for_review',
    priority: 'MEDIUM',
    assignedPrincipalId: 'principal-vault-officer-01',
    linkedOpportunityId: 'opp-sec-803',
    dueAtIso: '2026-08-24T11:00:00Z',
    checklist: [
      { item: 'Verify Original Ministry of Lands Title Deed Watermark & Stamp', completed: true },
      { item: 'Enroll Counsel Nabukeera Iris and Fingerprint Biometrics into Vault Mantrap', completed: true },
      { item: 'Seal document container in inert argon-gas fireproof capsule', completed: true },
      { item: 'Issue tamper-evident client receipt token with SHA-256 evidence proof', completed: false },
    ],
  },
];
